import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import type { User } from '@/services/authService';
import { logAction } from '@/services/logService';

// 临时mock的API调用，后续需要替换为真实的API
const getCurrentUser = async (): Promise<{ success: boolean; user: User }> => {
  // Mock用户数据
  return {
    success: true,
    user: {
      id: '1',
      username: 'admin',
      email: 'admin@example.com',
      role: 'super_admin',
      permissions: ['dashboard:view', 'dashboard:execute', 'product:sync', 'product:price_sync'],
      status: 'active',
      created_at: new Date().toISOString(),
      last_login: new Date().toISOString(),
    }
  };
};

const refreshToken = async (): Promise<{ success: boolean; token: string; expires_in: number }> => {
  // Mock token刷新
  return {
    success: true,
    token: 'mock_refreshed_token',
    expires_in: 3600,
  };
};

interface UserContextType {
  user: User | null;
  loading: boolean;
  login: (user: User, token: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

interface UserProviderProps {
  children: ReactNode;
}

export const UserProvider: React.FC<UserProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // 从localStorage获取token
  const getStoredToken = (): string | null => {
    return localStorage.getItem('auth_token');
  };

  // 从localStorage获取用户数据
  const getStoredUser = (): User | null => {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  };

  // 存储token到localStorage
  const storeToken = (token: string): void => {
    localStorage.setItem('auth_token', token);
  };

  // 存储用户数据到localStorage
  const storeUser = (userData: User): void => {
    localStorage.setItem('user', JSON.stringify(userData));
  };

  // 清除存储的token和用户数据
  const clearStoredToken = (): void => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
  };

  // 登录
  const login = (userData: User, token: string) => {
    const normalizedUser = {
      ...userData,
      role: (userData.role || '').trim().toLowerCase(),
    };
    setUser(normalizedUser);
    storeToken(token);
    storeUser(normalizedUser);
  };

  // 登出
  const logout = async () => {
    const currentUser = user;
    
    // 记录登出操作
    if (currentUser) {
      try {
        await logAction({
          user: currentUser.username,
          action: 'User Logout',
          task_path: '/auth/logout',
          task_name: 'User Authentication',
          parameters: {
            username: currentUser.username,
            result: 'success'
          },
          result: 'success',
          message: `User ${currentUser.username} logged out successfully`,
          duration: 0,
        });
      } catch (error) {
        console.warn('Failed to log logout action:', error);
      }
    }
    
    setUser(null);
    clearStoredToken();
  };

  // 刷新用户信息
  const refreshUser = async () => {
    try {
      const response = await getCurrentUser();
      if (response.success) {
        setUser(response.user);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      logout();
    }
  };

  // 初始化用户状态
  useEffect(() => {
    const initializeUser = async () => {
      const token = getStoredToken();
      const storedUser = getStoredUser();
      
      if (token && storedUser) {
        // 如果有token和用户数据，直接设置用户状态
        setUser(storedUser);
      } else if (token) {
        try {
          // 如果只有token，尝试获取用户信息
          const response = await getCurrentUser();
          if (response.success) {
            setUser(response.user);
            storeUser(response.user);
          } else {
            // 如果获取失败，尝试刷新token
            try {
              const refreshResponse = await refreshToken();
              if (refreshResponse.success) {
                storeToken(refreshResponse.token);
                const userResponse = await getCurrentUser();
                if (userResponse.success) {
                  setUser(userResponse.user);
                  storeUser(userResponse.user);
                } else {
                  logout();
                }
              } else {
                logout();
              }
            } catch (refreshError) {
              console.error('Failed to refresh token:', refreshError);
              logout();
            }
          }
        } catch (error) {
          console.error('Failed to initialize user:', error);
          logout();
        }
      }
      setLoading(false);
    };

    initializeUser();
  }, []);

  // 定期检查token有效性
  useEffect(() => {
    const checkTokenValidity = async () => {
      if (user && getStoredToken()) {
        try {
          await getCurrentUser();
        } catch (error) {
          console.error('Token expired or invalid:', error);
          logout();
        }
      }
    };

    // 每10分钟检查一次token有效性
    const interval = setInterval(checkTokenValidity, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [user]);

  const value: UserContextType = {
    user,
    loading,
    login,
    logout,
    refreshUser,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};